import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/cn";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const CYCLE_MS = 6000;
const RESUME_MS = 8000;

const STAGES = [
  {
    label: "Input received",
    detail: "Client uploads passport, bank statements, and supporting documents. The system timestamps every file on arrival, hashes its contents for tamper detection, and creates an immutable intake record. From this point forward, nothing can be altered without a logged override.",
    visual: (
      <div className="space-y-2">
        <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest">
          Uploaded documents
        </span>
        {[
          { name: "passport_scan.pdf", size: "2.4 MB" },
          { name: "bank_statements.pdf", size: "1.1 MB" },
          { name: "employment_letter.pdf", size: "340 KB" },
          { name: "utility_bill.pdf", size: "520 KB" },
        ].map((file) => (
          <div key={file.name} className="flex items-center justify-between gap-2 p-2 border border-[var(--color-border)] rounded text-xs font-[family-name:var(--font-mono)]">
            <div className="flex items-center gap-2">
              <span className="text-[var(--color-fg-tertiary)]">📄</span>
              <span>{file.name}</span>
            </div>
            <span className="text-[var(--color-fg-tertiary)]">{file.size}</span>
          </div>
        ))}
        <div className="flex items-center gap-3 mt-1">
          <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] bg-[var(--color-surface)] px-2 py-1 border border-[var(--color-border)] rounded">
            2026-03-21T09:14:22Z
          </span>
          <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
            SHA-256 verified
          </span>
        </div>
      </div>
    ),
  },
  {
    label: "Agent processes",
    detail: "XENI reads every uploaded document, extracts structured data, and cross-references fields across sources. It confirms identity details match between the passport and bank statements, then flags a six-month employment gap for further review.",
    visual: (
      <div className="space-y-2">
        <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest">
          Extracted fields
        </span>
        <div className="space-y-1">
          {["Full name", "Date of birth", "Passport number", "Nationality", "Employer", "Annual income"].map((f) => (
            <div key={f} className="flex items-center gap-2 text-xs">
              <span className="text-emerald-500">✓</span>
              <span className="text-[var(--color-fg-secondary)]">{f}</span>
            </div>
          ))}
        </div>
        <div className="inline-flex items-center gap-1.5 text-xs font-[family-name:var(--font-mono)] text-amber-500 bg-amber-500/10 px-2 py-1 rounded">
          <span>⚠</span>
          <span>Employment gap: Mar–Sep 2024</span>
        </div>
      </div>
    ),
  },
  {
    label: "Compliance gate",
    detail: "The case is evaluated against five automated compliance checkpoints before it can proceed. Identity, document completeness, sanctions, and PEP screening all pass. The employment continuity check triggers an amber flag, routing the case to a qualified human reviewer.",
    visual: (
      <div className="space-y-2">
        <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest">
          Checkpoint results
        </span>
        {[
          { check: "Identity verification", status: "pass" as const },
          { check: "Document completeness", status: "pass" as const },
          { check: "Sanctions screening", status: "pass" as const },
          { check: "PEP check", status: "pass" as const },
          { check: "Employment continuity", status: "warn" as const },
        ].map((c) => (
          <div key={c.check} className="flex items-center gap-2 text-xs">
            <span
              className={cn(
                "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                c.status === "pass"
                  ? "bg-emerald-500/20 text-emerald-500"
                  : "bg-amber-500/20 text-amber-500",
              )}
            >
              {c.status === "pass" ? "✓" : "⚠"}
            </span>
            <span className="text-[var(--color-fg-secondary)]">{c.check}</span>
          </div>
        ))}
        <div className="text-[10px] font-[family-name:var(--font-mono)] text-amber-500 mt-1">
          → Routed to human review
        </div>
      </div>
    ),
  },
  {
    label: "Human review",
    detail: "The flagged employment gap is routed to a qualified immigration solicitor. They review the supporting employer letter, confirm the gap is explained by parental leave, and approve the case. Every annotation and decision is timestamped and attributed to the reviewer.",
    visual: (
      <div className="space-y-3">
        <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest">
          Reviewer
        </span>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[var(--color-fg-tertiary)]/20 flex items-center justify-center text-[10px] text-[var(--color-fg-secondary)]">
            JR
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-[var(--color-fg)]">J. Richardson</span>
            <span className="text-[10px] text-[var(--color-fg-tertiary)]">Immigration Solicitor, SRA #619284</span>
          </div>
        </div>
        <div className="space-y-2 mt-1">
          <div className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
            Flag reviewed: Employment continuity
          </div>
          <p className="text-xs text-[var(--color-fg-tertiary)] italic border-l-2 border-[var(--color-border)] pl-2">
            "Gap explained by parental leave — Mar to Sep 2024. Employer letter confirms return to same role. No further action required."
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-block text-xs font-[family-name:var(--font-mono)] font-medium text-emerald-500 bg-emerald-500/10 px-3 py-1 rounded border border-emerald-500/20">
              Approved
            </span>
            <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
              09:22:17
            </span>
          </div>
        </div>
      </div>
    ),
  },
  {
    label: "Audit report",
    detail: "The completed application is submitted with a full audit trail attached. Every extraction, flag, human decision, and approval is recorded with timestamps and attribution. The trail is cryptographically signed, immutable, and exportable for regulatory review.",
    visual: (
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest">
            Audit Trail
          </span>
          <span className="inline-block text-[10px] font-[family-name:var(--font-mono)] font-medium text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
            Complete
          </span>
        </div>
        <div className="space-y-0 text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
          {[
            { time: "09:14:22", event: "Documents received", who: "System" },
            { time: "09:15:01", event: "Data extraction complete", who: "XENI" },
            { time: "09:15:03", event: "Employment gap flagged", who: "XENI" },
            { time: "09:15:04", event: "Routed to human review", who: "Compliance gate" },
            { time: "09:22:17", event: "Gap approved — parental leave", who: "J. Richardson" },
            { time: "09:23:05", event: "Application submitted", who: "System" },
          ].map((entry) => (
            <div key={entry.time} className="flex items-start gap-3 py-1 border-l border-[var(--color-border)] pl-3">
              <span className="flex-shrink-0 tabular-nums">{entry.time}</span>
              <span className="flex-1 text-[var(--color-fg-secondary)]">{entry.event}</span>
              <span className="flex-shrink-0">{entry.who}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
];

const SUMMARY_CARDS = [
  {
    title: "Audit trails",
    description:
      "Every decision, extraction, and human override is logged with timestamps and attribution. Trails are immutable and exportable for regulatory review.",
  },
  {
    title: "Human oversight",
    description:
      "AI handles volume. Humans handle judgment. Every flagged item routes to a qualified reviewer before it leaves the system.",
  },
  {
    title: "Compliance gates",
    description:
      "Automated checkpoints enforce regulatory requirements at each stage. Cases cannot progress until all checks pass or are explicitly overridden by an authorised reviewer.",
  },
];

export function GovernanceJourney() {
  const [activeStage, setActiveStage] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const playing = visible && !userPaused && !prefersReducedMotion;

  // Track visibility
  useEffect(() => {
    if (prefersReducedMotion) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!!entry?.isIntersecting),
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  // Auto-cycle
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setActiveStage((prev) => {
        const next = prev < STAGES.length - 1 ? prev + 1 : 0;
        setProgressKey((k) => k + 1);
        return next;
      });
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [playing]);

  const handleStageClick = useCallback(
    (i: number) => {
      setActiveStage(i);
      setProgressKey((k) => k + 1);
      setUserPaused(true);

      if (resumeTimer.current) clearTimeout(resumeTimer.current);
      if (!prefersReducedMotion) {
        resumeTimer.current = setTimeout(() => setUserPaused(false), RESUME_MS);
      }
    },
    [prefersReducedMotion],
  );

  const handleNext = useCallback(() => {
    setActiveStage((prev) => {
      const next = prev < STAGES.length - 1 ? prev + 1 : 0;
      setProgressKey((k) => k + 1);
      return next;
    });
    setUserPaused(true);

    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    if (!prefersReducedMotion) {
      resumeTimer.current = setTimeout(() => setUserPaused(false), RESUME_MS);
    }
  }, [prefersReducedMotion]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    };
  }, []);

  const isLastStage = activeStage === STAGES.length - 1;
  const stepPct = 100 / (STAGES.length - 1);
  const fromPct = activeStage * stepPct;
  const toPct = isLastStage ? 100 : (activeStage + 1) * stepPct;

  return (
    <div ref={containerRef}>
      {/* Case badge */}
      <div className="mb-8">
        <span className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] border border-[var(--color-border)] px-2 py-1 rounded">
          Case #1847
        </span>
      </div>

      {/* Pipeline — horizontal on desktop, vertical on mobile */}
      <div className="relative mb-8">
        {/* Desktop pipeline */}
        <div className="hidden md:flex items-center justify-between relative">
          {/* Background line */}
          <div className="absolute top-4 left-4 right-4 h-0.5 bg-[var(--color-border)]" />
          {/* Completed progress line */}
          <div
            className="absolute top-4 left-4 h-0.5 bg-[var(--color-fg)]"
            style={{
              width: `${fromPct}%`,
              maxWidth: "calc(100% - 32px)",
              transition: prefersReducedMotion ? "none" : "width 500ms ease-out",
            }}
          />
          {/* Animated segment to next stage */}
          {playing && (
            <div
              key={progressKey}
              className="absolute top-4 left-4 h-0.5 bg-[var(--color-fg)]"
              style={{
                maxWidth: "calc(100% - 32px)",
                animation: `gov-h-progress ${CYCLE_MS}ms linear forwards`,
              }}
            />
          )}
          {STAGES.map((stage, i) => (
            <button
              key={stage.label}
              onClick={() => handleStageClick(i)}
              className="relative z-10 flex flex-col items-center cursor-pointer group"
            >
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-[family-name:var(--font-mono)] border-2 transition-all",
                  i < activeStage
                    ? "bg-[var(--color-fg-secondary)] border-[var(--color-fg-secondary)] text-[var(--color-bg)]"
                    : i === activeStage
                      ? "bg-[var(--color-fg)] border-[var(--color-fg)] text-[var(--color-bg)] scale-110"
                      : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-fg-tertiary)] group-hover:border-[var(--color-fg-secondary)]",
                )}
                style={{ transition: prefersReducedMotion ? "none" : undefined }}
              >
                {i + 1}
              </div>
              <span
                className={cn(
                  "text-[10px] font-[family-name:var(--font-mono)] mt-2 whitespace-nowrap",
                  i <= activeStage
                    ? "text-[var(--color-fg)]"
                    : "text-[var(--color-fg-tertiary)]",
                )}
              >
                {stage.label}
              </span>
            </button>
          ))}
        </div>

        {/* Mobile pipeline — vertical */}
        <div className="md:hidden flex flex-col gap-0 relative">
          {/* Background line */}
          <div className="absolute top-4 bottom-4 left-[15px] w-0.5 bg-[var(--color-border)]" />
          {/* Completed progress line */}
          <div
            className="absolute top-4 left-[15px] w-0.5 bg-[var(--color-fg)]"
            style={{
              height: `${fromPct}%`,
              maxHeight: "calc(100% - 32px)",
              transition: prefersReducedMotion ? "none" : "height 500ms ease-out",
            }}
          />
          {/* Animated segment to next stage */}
          {playing && (
            <div
              key={progressKey}
              className="absolute top-4 left-[15px] w-0.5 bg-[var(--color-fg)]"
              style={{
                maxHeight: "calc(100% - 32px)",
                animation: `gov-v-progress ${CYCLE_MS}ms linear forwards`,
              }}
            />
          )}
          {STAGES.map((stage, i) => (
            <button
              key={stage.label}
              onClick={() => handleStageClick(i)}
              className="relative z-10 flex items-center gap-3 py-2 cursor-pointer group"
            >
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-[family-name:var(--font-mono)] border-2 flex-shrink-0 transition-all",
                  i < activeStage
                    ? "bg-[var(--color-fg-secondary)] border-[var(--color-fg-secondary)] text-[var(--color-bg)]"
                    : i === activeStage
                      ? "bg-[var(--color-fg)] border-[var(--color-fg)] text-[var(--color-bg)] scale-110"
                      : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-fg-tertiary)] group-hover:border-[var(--color-fg-secondary)]",
                )}
                style={{ transition: prefersReducedMotion ? "none" : undefined }}
              >
                {i + 1}
              </div>
              <span
                className={cn(
                  "text-xs font-[family-name:var(--font-mono)]",
                  i <= activeStage
                    ? "text-[var(--color-fg)]"
                    : "text-[var(--color-fg-tertiary)]",
                )}
              >
                {stage.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Detail panel — crossfade */}
      <div className="border border-[var(--color-border)] p-6 md:p-8 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {/* Left: text + next button */}
          <div className="relative flex flex-col min-h-[14rem]">
            <div className="relative flex-1 min-h-[9rem]">
              {STAGES.map((stage, i) => (
                <div
                  key={stage.label}
                  className={cn(
                    prefersReducedMotion ? "" : "transition-opacity duration-400 ease-out",
                    i === 0 ? "relative" : "absolute inset-0",
                  )}
                  style={{
                    opacity: activeStage === i ? 1 : 0,
                    pointerEvents: activeStage === i ? "auto" : "none",
                  }}
                >
                  <h3 className="text-xl font-medium mb-3 font-[family-name:var(--font-display)]">
                    {stage.label}
                  </h3>
                  <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
                    {stage.detail}
                  </p>
                </div>
              ))}
            </div>
            {/* Next / Restart button */}
            <div className="mt-auto pt-4 border-t border-[var(--color-border)]">
              {isLastStage ? (
                <button
                  onClick={() => handleStageClick(0)}
                  className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors cursor-pointer"
                >
                  Restart case →
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors cursor-pointer"
                >
                  Next step →
                </button>
              )}
            </div>
          </div>
          {/* Right: visual */}
          <div className="relative min-h-[14rem]">
            {STAGES.map((stage, i) => (
              <div
                key={stage.label}
                className={cn(
                  prefersReducedMotion ? "" : "transition-opacity duration-400 ease-out",
                  i === 0 ? "relative" : "absolute inset-0",
                )}
                style={{
                  opacity: activeStage === i ? 1 : 0,
                  pointerEvents: activeStage === i ? "auto" : "none",
                }}
              >
                {stage.visual}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SUMMARY_CARDS.map((card) => (
          <div
            key={card.title}
            className="border border-[var(--color-border)] p-6"
          >
            <h4 className="text-sm font-[family-name:var(--font-mono)] font-medium mb-2">
              {card.title}
            </h4>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
              {card.description}
            </p>
          </div>
        ))}
      </div>

      {/* Progress bar keyframes */}
      <style>{`
        @keyframes gov-h-progress {
          from { width: ${fromPct}%; }
          to { width: ${toPct}%; }
        }
        @keyframes gov-v-progress {
          from { height: ${fromPct}%; }
          to { height: ${toPct}%; }
        }
      `}</style>
    </div>
  );
}
